import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { clearRoundTrail, selectIsReviewing, selectLiveGame, selectRoundMotion } from "src/store/game"
import { MAX_PLAY_MS } from "src/utils/roundMotion"

import "./RoundFlash.css"

// Long enough to catch an eye that was somewhere else on the map, short enough that it is gone
// before it is in the way. It leaves on its own — a banner that needed dismissing would be a
// dialog, and this is an announcement. Since 2026-10-05 the roads of the round that just turned
// are drawn on the map while it shows (the store's `roundTrail`, which arrives on the same socket
// frame as the board), so it stays long enough to read them: "a few seconds" (owner). Since
// 2026-10-23 it is the **floor**: the caption waits for the round's playback to end (requirement
// 36, M3), and a show nobody plays cannot hold it past `MAX_HOLD_MS`.
export const ROUND_FLASH_MS = 4000
export const MAX_HOLD_MS = ROUND_FLASH_MS + MAX_PLAY_MS + 1000
// The fade on the way out; the component stays mounted that long so the CSS has something to fade.
export const LEAVE_MS = 350

/* "The round turned" — the one thing the game never said out loud (owner, 2026-09-09: "no visual
   indicator that round has finished in the game").

   A resolved round already made a *sound*, and it opened the round digest — but only when the
   round did something to this player. So a tick that passed you by, which is most of the opening,
   changed the board and the clock and announced itself nowhere: a player planning a march looked
   up to find their orders had gone and the numbers had moved, with nothing having marked the
   moment. The top bar's round counter is a number that changes while you are not looking at it.

   Deliberately not a modal and not a toast. A modal is what the digest is for, and it stops the
   game; a toast belongs in the corner with the errors and would be read as one. This is a caption
   across the board — the map is where the player is looking, and the round is a fact about the
   board rather than about anything they did. `pointer-events: none`, because an order it swallowed
   would cost more than the banner is worth.

   Nothing in review: stepping through snapshots changes the tick without a round having been
   played, so it would fire on every press of the stepper.

   It waits for the show (requirement 36, M3). The round's motion log is in the store while the
   Game view plays it, keyed by the round, and cleared — by its round — when the show ends; while
   the log for the announced round is there, past the floor, the caption and the trail stay, so the
   arrows are the roads being walked under the moving sprites. */
const RoundFlash = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // The live game's tick, not the board on screen: stepping back to a past round (requirement 35)
  // changes the tick drawn without a round having turned, and so does coming back to the present.
  const game = useSelector(selectLiveGame)
  const reviewing = useSelector(selectIsReviewing)
  const roundMotion = useSelector(selectRoundMotion)
  const tick = game?.tick
  const [shown, setShown] = useState(null)
  // "floor" once ROUND_FLASH_MS has passed, "capped" once nothing may hold it any longer.
  const [phase, setPhase] = useState(null)
  const [leaving, setLeaving] = useState(false)
  const lastTick = useRef(null)

  useEffect(() => {
    if (tick == null || lastTick.current === tick) return
    const first = lastTick.current === null
    lastTick.current = tick
    // Not on arrival: opening a game mid-war is not a round resolving.
    if (first || reviewing) return
    setShown(tick)
    setPhase(null)
    setLeaving(false)
    const floor = setTimeout(() => setPhase("floor"), ROUND_FLASH_MS)
    const cap = setTimeout(() => setPhase("capped"), MAX_HOLD_MS)
    return () => {
      clearTimeout(floor)
      clearTimeout(cap)
      // The trail of this round is already in the store: `game_next_round` carried it beside the
      // board (2026-10-17). Named, because this cleanup runs after the next round's trail has
      // landed and must not take that one down.
      dispatch(clearRoundTrail({ tick }))
    }
    // The tick is what this watches; re-running on `reviewing` would fire the banner when a
    // finished game flips into review on its own last round.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])

  // Past the floor, the caption goes when the show has — or at the cap, whatever is still playing.
  const held = shown != null && roundMotion?.tick === shown
  useEffect(() => {
    if (shown == null || phase == null) return undefined
    if (held && phase !== "capped") return undefined
    // Fading, then gone: the fade needs the element to still be there. Not re-run on `leaving`
    // itself, or setting it would clear this timer on the way out.
    setLeaving(true)
    const timer = setTimeout(() => {
      setShown(null)
      setLeaving(false)
      dispatch(clearRoundTrail({ tick: shown }))
    }, LEAVE_MS)
    return () => clearTimeout(timer)
  }, [shown, phase, held, dispatch])

  if (shown == null) return null

  return (
    <div className={`round-flash${leaving ? " round-flash-leaving" : ""}`} role="status" aria-live="polite" key={shown}>
      <span className="round-flash-label">{t("game.roundFlash.label")}</span>
      <span className="round-flash-number">{shown}</span>
    </div>
  )
}

export default RoundFlash
